import { useRef, type ReactNode } from 'react';
import { SECTION_AREA_TOP, SECTION_STACK } from './geometry';
import { QuoteSection } from './QuoteSection';
import { QuoteTabs, tabButtonId, tabIdOf, tabPanelId, type QuoteTab } from './QuoteTabs';
import type { QuoteLayoutSection } from '../types';
import { cn } from '../ui/primitives';

/**
 * One part of the quote at a time, behind a strip.
 *
 * The layout for doing one job to a quote, where the other sections are just
 * distance. Sections are grouped into tabs by `tabGroup` (see `toTabs`), so a
 * tab can stand for several of them.
 *
 * ── Why nothing unmounts ──
 *
 * Every section renders; the ones outside the open tab are hidden. Sections on
 * this page feed each other — on the admin quote, the offer section owns the
 * batches query and reports the offer's value range up to the stat strip in the
 * page header, whether any offer has been sent (which decides what the client
 * section offers), and the batches the client-preview modal previews.
 * Unmounting it on a tab switch would blank numbers that have nothing to do
 * with the tab you just closed, and refetch them when you came back. Hiding
 * costs one render of a section nobody is looking at.
 *
 * The consequence to remember: hidden panels have no box. Anything measuring or
 * scrolling to a section has to open its tab first — which is what
 * `QuoteSections`' `revealRef` is for.
 */
export function TabsLayout({
	sections,
	tabs,
	openTab,
	onOpenTab,
	lead,
	className,
}: {
	sections: QuoteLayoutSection[];
	tabs: QuoteTab[];
	openTab: string;
	onOpenTab: (id: string) => void;
	lead?: ReactNode;
	className?: string;
}) {
	const strip = useRef<HTMLDivElement>(null);

	/* Land at the top of the tab you opened — but only when the strip has already
	   scrolled off, so clicking a tab from the top of the page doesn't yank the
	   header out of view. 72px is where the page's sticky header ends. */
	const handleSelect = (id: string) => {
		onOpenTab(id);
		const el = strip.current;
		if (el && el.getBoundingClientRect().top < 72) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	return (
		<div className={cn(SECTION_AREA_TOP, className)}>
			{/* Above the strip, not inside a tab: an alert that has to be seen
			    whichever part of the quote is open. */}
			{lead && <div className="mb-8">{lead}</div>}

			<div ref={strip} className="mb-8 scroll-mt-[72px]">
				<QuoteTabs tabs={tabs} active={openTab} onSelect={handleSelect} />
			</div>

			{/* One panel per TAB rather than per section, so a tab covering four
			    sections is announced as one region instead of four each claiming to
			    be the panel the tab controls. */}
			{tabs.map((tab) => (
				<div
					key={tab.id}
					id={tabPanelId(tab.id)}
					role="tabpanel"
					aria-labelledby={tabButtonId(tab.id)}
					className={cn(tab.id !== openTab && 'hidden')}
				>
					<div className={SECTION_STACK}>
						{sections
							.filter((section) => tabIdOf(section) === tab.id)
							.map((section) => (
								<QuoteSection key={section.id} id={section.id}>
									{section.content}
								</QuoteSection>
							))}
					</div>
				</div>
			))}
		</div>
	);
}
